import { clsx } from 'clsx';
import styles from './Shortcut.module.scss';

/**
 * Detects if the user is on a Mac or PC based on the navigator platform.
 */
const isMac = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return /Mac|iPhone|iPod|iPad/i.test(navigator.platform);
};

/**
 * Mapping of common key names to their Unicode symbols.
 * Different mappings for Mac and PC platforms.
 */
const MAC_KEY_SYMBOLS: Record<string, string> = {
  // Modifiers
  command: '⌘',
  cmd: '⌘',
  shift: '⇧',
  option: '⌥',
  alt: '⌥',
  control: '⌃',
  ctrl: '⌃',

  // Special keys
  enter: '↵',
  return: '↵',
  delete: '⌫',
  backspace: '⌫',
  escape: 'esc',
  tab: '⇥',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: '␣',
};

const PC_KEY_SYMBOLS: Record<string, string> = {
  // Modifiers - PC uses text for most
  command: 'Ctrl',
  cmd: 'Ctrl',
  shift: '⇧',
  option: 'Alt',
  alt: 'Alt',
  control: 'Ctrl',
  ctrl: 'Ctrl',

  // Special keys
  enter: '↵',
  return: '↵',
  delete: 'Del',
  backspace: '⌫',
  escape: 'Esc',
  esc: 'Esc',
  tab: '⇥',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: '␣',
};

/**
 * Normalizes a key string to its display representation.
 * Converts known key names to symbols (Mac) or text (PC), otherwise returns the key in uppercase.
 */
const normalizeKey = (key: string, platform: 'mac' | 'pc'): string => {
  const lowerKey = key.toLowerCase().trim();
  const symbols = platform === 'mac' ? MAC_KEY_SYMBOLS : PC_KEY_SYMBOLS;
  return symbols[lowerKey] ?? key.toUpperCase();
};

export type ShortcutProps = {
  /**
   * The keyboard shortcut(s) to display.
   * Can be a single key or an array of keys.
   *
   * Common modifiers are automatically converted to symbols (Mac) or text (PC):
   * - "command" or "cmd" → ⌘ (Mac) / Ctrl (PC)
   * - "shift" → ⇧
   * - "option" or "alt" → ⌥ (Mac) / Alt (PC)
   * - "control" or "ctrl" → ⌃ (Mac) / Ctrl (PC)
   *
   * Special keys like "enter", "escape", "delete" are also supported.
   *
   * @example
   * ```tsx
   * <Shortcut keys="k" />
   * <Shortcut keys={['cmd', 'k']} />
   * <Shortcut keys={['shift', 'cmd', 'p']} />
   * ```
   */
  keys: string | string[];

  /**
   * Visual variant for the shortcut.
   * - "dark": For use on dark backgrounds (e.g., in tooltips, dark menus)
   * - "light": For use on light backgrounds (e.g., in page content, secondary buttons)
   * - "color": For use on colored button backgrounds (e.g., primary, danger, brand buttons)
   * @default "light"
   */
  variant?: 'dark' | 'light' | 'color';

  /**
   * Custom className for styling overrides.
   */
  className?: string;
};

/**
 * Displays keyboard shortcuts with proper styling and Unicode symbols for
 * modifier keys. Automatically detects Mac vs PC platform and displays
 * appropriate key symbols. Designed to be used inline within tooltips and button labels.
 *
 * @example
 * ```tsx
 * // In a Tooltip (dark background)
 * <Tooltip content={<>Edit <Shortcut keys={['cmd', 'e']} variant="dark" /></>}>
 *   <IconButton name="edit" />
 * </Tooltip>
 *
 * // In a Button with light background
 * <Button variant="secondary">
 *   Save <Shortcut keys={['cmd', 's']} />
 * </Button>
 *
 * // In a Button with colored background
 * <Button variant="primary">
 *   Save <Shortcut keys={['cmd', 's']} variant="color" />
 * </Button>
 * ```
 */
export const Shortcut = ({
  keys,
  variant = 'light',
  className,
}: ShortcutProps) => {
  const keyArray = Array.isArray(keys) ? keys : [keys];
  const platform = isMac() ? 'mac' : 'pc';
  const normalizedKeys = keyArray.map((key) => normalizeKey(key, platform));

  return (
    <span className={clsx(styles.root, className)} data-zds>
      {normalizedKeys.map((key, keyIndex) => (
        <kbd
          // eslint-disable-next-line react/no-array-index-key
          key={`${normalizedKeys.join('-')}-${key}-${keyIndex}`}
          className={styles.key}
          data-variant={variant}
          data-zds
        >
          {key}
        </kbd>
      ))}
    </span>
  );
};
